import React, { Fragment, type JSX } from 'react';
import BuildsNoDataPlaceholder from '../BuildsNoDataPlaceholder/BuildsNoDataPlaceholder';
import EnhancedMarkdown from '../EnhancedMarkdown/EnhancedMarkdown';
import buildsEnhancedMarkdownOverrides from '@resources/js/helpers/buildsEnhancedMarkdownOverrides';
import { type Options as HighchartsOptions } from 'highcharts';
import Chart from '../Chart/Chart';

type BuildsStatPriorityDatumViewModel = {
  sampleCount: number;
  interval: { upperEnd: number; lowerEnd: number };
};

export type BuildsStatPriorityViewModel = {
  name: string;
  interval: null;
  order: number;
  data: BuildsStatPriorityDatumViewModel[];
  value: number;
};

export type BuildsStatPrioritySectionProps = {
  title: string;
  description: string;
  stats: BuildsStatPriorityViewModel[];
};

export default function BuildsStatPrioritySection({
  title,
  description,
  stats,
}: BuildsStatPrioritySectionProps): JSX.Element {
  const statBoundaries = deriveStatBoundaries(stats);

  const statGroups = stats.reduce<BuildsStatPriorityViewModel[][]>(
    (acc, stat) => {
      const prev = acc[acc.length - 1];

      if (!prev || prev[prev.length - 1].order !== stat.order) {
        acc.push([stat]);
      } else {
        prev.push(stat);
      }

      return acc;
    },
    [],
  );

  return (
    <div className='builds-stat-priority-section'>
      <EnhancedMarkdown
        markdown={`
# ${title}

${description}
`}
        overrides={buildsEnhancedMarkdownOverrides}
      />
      {stats.length > 0 ? (
        <div className='builds-stat-priority-section__container'>
          {statGroups.map((statGroup, index) => {
            return (
              <Fragment key={statGroup.map((stat) => stat.name).join('|')}>
                {index > 0 ? <Arrow /> : null}
                <div className='builds-stat-priority-section__container'>
                  {statGroup.map((stat) => {
                    return (
                      <div
                        className='builds-stat-priority-section__container__inner'
                        key={stat.name}
                      >
                        <div className='builds-stat-priority-section__container__inner__chart-wrapper'>
                          <div className='builds-stat-priority-section__container__inner__chart-wrapper__chart'>
                            <Chart
                              options={createOptions(
                                stat,
                                statBoundaries,
                                index + 1,
                              )}
                            />
                          </div>
                          <StatBox stat={stat} priority={index + 1} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Fragment>
            );
          })}
        </div>
      ) : (
        <BuildsNoDataPlaceholder marginTop={1} />
      )}
    </div>
  );
}

type StatBoundaries = {
  lowerEnd: number;
  upperEnd: number;
};

function deriveStatBoundaries(
  stats: BuildsStatPriorityViewModel[],
): StatBoundaries {
  const statBoundaries = {
    lowerEnd: Number.POSITIVE_INFINITY,
    upperEnd: Number.NEGATIVE_INFINITY,
  };

  for (const stat of stats) {
    for (const dataset of stat.data) {
      if (dataset.interval.lowerEnd < statBoundaries.lowerEnd) {
        statBoundaries.lowerEnd = dataset.interval.lowerEnd;
      }

      if (dataset.interval.upperEnd > statBoundaries.upperEnd) {
        statBoundaries.upperEnd = dataset.interval.upperEnd;
      }
    }
  }

  return statBoundaries;
}

function createOptions(
  stat: BuildsStatPriorityViewModel,
  statBoundaries: StatBoundaries,
  priority: number,
): HighchartsOptions {
  const columns: number[] = [];
  const categories: string[] = [];

  // mainstat renders an empty graph due to having no data
  if (stat.data.length > 0) {
    const statBin =
      stat.data[0].interval.upperEnd - stat.data[0].interval.lowerEnd;

    if (statBin > 0) {
      for (
        let i = statBoundaries.lowerEnd;
        i <= statBoundaries.upperEnd;
        i += statBin
      ) {
        const match = stat.data.find(
          (dataset) =>
            dataset.interval.lowerEnd >= i &&
            dataset.interval.upperEnd <= i + statBin,
        );

        const lowerEnd = match ? match.interval.lowerEnd : i;
        const upperEnd = match ? match.interval.upperEnd : i + statBin;

        columns.push(match ? match.sampleCount : 0);
        categories.push(`${upperEnd} - ${lowerEnd} ${stat.name}`);
      }
    }
  }

  return {
    chart: {
      type: 'column',
      height: 50,
      margin: 0,
      backgroundColor: '',
      borderWidth: 0,
      width: 134,
    },
    tooltip: { outside: true },
    legend: {
      enabled: false,
    },
    xAxis: {
      visible: false,
      categories: categories.reverse(),
    },
    yAxis: {
      visible: false,
    },
    plotOptions: {
      column: {
        animation: false,
        crisp: false,
        borderWidth: 0.1,
        borderColor: '#000',
        pointPadding: 0.1,
        groupPadding: 0,
        className: `priority-${priority}`,
      },
    },
    series: [
      {
        name: 'Parses',
        data: columns.reverse(),
        type: 'column',
      },
    ],
  };
}

function Arrow() {
  return (
    <svg
      className='builds-stat-priority-section__container__arrow'
      width='29'
      height='39'
      viewBox='0 0 29 39'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M4 4.5L25 19.6145L4 34.5'
        stroke='#DEDEDE'
        strokeWidth='8'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}

const priorityToColorMap: Record<number, string> = {
  1: 'legendary',
  2: 'epic',
  3: 'rare',
};

type StatBoxProps = {
  stat: BuildsStatPriorityViewModel;
  priority: number;
};

function StatBox({ stat, priority }: StatBoxProps): JSX.Element {
  const color = priorityToColorMap[priority];

  return (
    <>
      <div
        className={`builds-stat-priority-section__container__stat-box ${
          color
            ? `builds-stat-priority-section__container__stat-box--${color}`
            : ''
        }`}
      >
        <div className='builds-stat-priority-section__container__stat-box__label'>
          {stat.name}
        </div>
      </div>
      <span className='builds-stat-priority-section__container__stat-box__value-wrapper'>
        {stat.value > 0 ? (
          <div className='builds-stat-priority-section__container__stat-box__value'>
            {stat.value}
          </div>
        ) : null}
      </span>
    </>
  );
}
